'use client';

import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ProductLoadingKeys, updateAmazonByProduct, updateReplenishableByProduct, updateStatusByProduct } from '../../slices/productsSlice';
import { unwrapResult } from '@reduxjs/toolkit';
import { addEDTOptionRequest, EDTLoadingKeys, getEDTOptionsRequest } from '../../slices/edtSlice';
import { ValueSelect } from '../ValueSelect';
import { getStatusColor, StatusSelect } from '../StatusSelect';
import { addProductCategoriesOptionRequest, getProductCategoriesOptionsRequest, ProductCategoriesLoadingKeys } from '../../slices/productCategoriesSlice';
import { SaveCancelByIconButtons } from '../SaveCancelByIconButtons';
import { InformationCircleIcon, XMarkIcon, TrashIcon } from '@heroicons/react/24/outline';
import { LoaderSpinner } from '../LoaderSpinner';
import { ProductType } from '../../consts/productTypes';
import ReactTooltip from 'react-tooltip';
import { arrayHasItems } from '../../helpers/common';
import { deleteVariantFromGroup, ProductGroupLoadingKeys, updateSingleProductInGroup, addVariantToGroup, deleteGroup } from '../../slices/productGroupSlice';
import { OverlayGrayBackground } from '../OverlayGrayBackground';
import { InventoryProductsSidebar } from '../InventoryProductsSidebar';
import VariantModal from './VariantModal';
import { ConfrimModal } from '../ConfrimModal';
import { IsReplanishable } from '../common/IsReplanishable';
import { IsAmazon } from '../common/IsAmazon';
import { ConnectVariantButton } from '../common/ConnectVariantButton';
import { ACTIVE } from '../../consts/inventoryStatus';

const ProductsTable = ({
  products,
  access,
  openCreationVariant,
  openVariantSidebarCallback,
  onSaveCreation,
  onCloseProductCreation,
}) => {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productType, setProductType] = useState(null);
  const [billOfMaterials, setBillOfMaterials] = useState([]);
  const [activeTab, setActiveTab] = useState('details');
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [variantBillOfMaterials, setVariantBillOfMaterials] = useState([]);
  const [productComponents, setProductComponents] = useState([]);
  const [isComponentModalOpen, setIsComponentModalOpen] = useState(false);
  const [editBarcode, setEditBarcode] = useState(false);
  const [componentSearchTerm, setComponentSearchTerm] = useState('');
  const [productsState, setProductsState] = useState([...products]);
  const [barcodeValue, setBarcodeValue] = useState("")
  const [leadTimeValue, setLeadTimeValue] = useState(null)
  const [expandedRows, setExpandedRows] = useState({});
  const [currentGroup, setCurrentGroup] = useState("");
  const [isVariantModalOpen, setIsVariantModalOpen] = useState(false);
  const [modalCurrentGroup, setModalCurrentGroup] = useState(null);
  const [deleteVariantModalOpened, setDeleteVariantModalOpened] = useState(false);
  const [deleteGroupModalOpened, setDeleteGroupModalOpened] = useState(false);
  const [variantIdToDelete, setVariantIdToDelete] = useState("");
  const [groupIdToDelete, setGroupIdToDelete] = useState("");
  const [groupIdForVariantDelete, setGroupIdForVariantDelete] = useState("");
  const [vendorIdToDelete, setVendorIdToDelete] = useState("");
  const [selectedTab, setSelectedTab] = useState({
    group: 'details',
    variant: 'details'
  });
  const barcodeRef = useRef(null);
  const deleteButtonRef = useRef({});
  const dispatch = useDispatch();
  const {
    loading: groupLoading,
    loadingKeys: groupLoadingKeys,
    searchedVariants: searchedVariantsByGroup,
  } = useSelector((state) => state.productGroup);
  const {
    loading: productLoading,
    loadingKeys: productLoadingKeys
  } = useSelector((state) => state.products);
  const productStatusIsLoading = () => 
    productLoading && productLoadingKeys.includes(ProductLoadingKeys.updateStatus);
  const productAmazonIsLoading = () => 
    productLoading && productLoadingKeys.includes(ProductLoadingKeys.updateAmazon);
  const productReplanishableIsLoading = () => 
    productLoading && productLoadingKeys.includes(ProductLoadingKeys.updateReplanishable);
  const deleteVariantIsLoading = () =>
    groupLoading && groupLoadingKeys.includes(ProductGroupLoadingKeys.deleteVariant);
  const deleteGroupIsLoading = () =>
    groupLoading && groupLoadingKeys.includes(ProductGroupLoadingKeys.deleteGroup);
  const addVariantIsLoading = () =>
    groupLoading && groupLoadingKeys.includes(ProductGroupLoadingKeys.addVariantToGroup);
  const searchedVariantsIsLoading = () => 
    groupLoading && groupLoadingKeys.includes(ProductGroupLoadingKeys.getVariants);
  // // Filter components based on search term
  // const filteredComponents = mockComponents.filter(component => 
  //   component.sku.toLowerCase().includes(componentSearchTerm.toLowerCase()) ||
  //   component.productName.toLowerCase().includes(componentSearchTerm.toLowerCase()) ||
  //   component.category.toLowerCase().includes(componentSearchTerm.toLowerCase())
  // );

  // const {
  //   loading: productsIsLoading,
  //   loadingKeys: productsLoadingKeys,
  // } = useSelector((state) => state.products);
  // const {
  //   loading: edtLoading,
  //   loadingKeys: edtLoadingKeys,
  //   options: edtOptions
  // } = useSelector((state) => state.edt);
  // const {
  //   loading: productCategoryLoading,
  //   loadingKeys: productCategoryKeys,
  //   options: productCategoriesOptions
  // } = useSelector((state) => state.productCategories);

  useEffect(() => {
    setProductsState(products);
    if (selectedProduct) {
      const isChildProduct = selectedProduct?.group_id;
      if (isChildProduct) {
        const group = products.find((product) => product.id === selectedProduct?.group_id);
        if (group) {
          setSelectedProduct((group.variants || []).find((variant) => variant.id === selectedProduct?.id))
        }
      } else {
        setSelectedProduct(products.find((product) => product.id === selectedProduct.id))
      }
    }
  }, [products]);

  useEffect(() => {
    ReactTooltip.rebuild()
    // dispatch(getEDTOptionsRequest({}));
    // dispatch(getProductCategoriesOptionsRequest({}))
  }, []);

  const handleRowClick = (product) => {
    setSelectedProduct(product);
    setProductType(ProductType.PRODUCT);
    // Set sample BOM data based on product ID if available
    // setBillOfMaterials(sampleBomData[product.id] || []);
    // Set sample components data if available
    // setProductComponents(sampleProductComponentsData[product.id] || []);
    // Reset tab to details when selecting a new product
    // setActiveTab('details');
    // Reset selected variant
    // setSelectedVariant(null);
    // setVariantBillOfMaterials([]);
  };

  const handleRowKeyDown = (product) => (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault(); // Prevent spacebar scroll
      setSelectedProduct(product);
    }
  };

  const renderBoolean = (value) => (value ? 'Y' : 'N');

  // Handle variant selection
  const handleVariantClick = (variant) => {
    setSelectedProduct(variant);
    setProductType(ProductType.PRODUCT_VARIANT);
    // setSelectedVariant(variant);
    // Initialize variant BOM with a copy of the product BOM
    // setVariantBillOfMaterials([...billOfMaterials]);
  };

  const handleDeleteVariant = async (productId, groupId, vendorId) => {
    if (access === "edit") {
      setVariantIdToDelete(productId);
      setGroupIdForVariantDelete(groupId);
      setVendorIdToDelete(vendorId);
      setDeleteVariantModalOpened(true);
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (access === "edit") {
      setGroupIdToDelete(groupId);
      setDeleteGroupModalOpened(true);
    }
  };

  const handleAddVariant = async (variant) => {
    if (modalCurrentGroup && access === "edit") {
      await dispatch(addVariantToGroup({
        groupId: modalCurrentGroup.id,
        vendorId: modalCurrentGroup.vendor_id,
        variantId: variant.id
      }));
      // Close modal after successful addition
      // setIsVariantModalOpen(false);
      // setVariantSearchTerm('');
      // setModalCurrentGroup(null);
    }
  };

  const handleStatusChange = async (productId, newStatus) => {
    if (access === "edit") {
      await dispatch(updateStatusByProduct({
        productId, 
        newStatus,
        requestToUpdate: updateSingleProductInGroup,
      }))
    }
  };

  const handleIsAmazonChange = async (productId, amazonValue) => {
    if (access === "edit") {
      await dispatch(updateAmazonByProduct({
        productId,
        isAmazonManually: amazonValue || false,
        requestToUpdate: updateSingleProductInGroup,
      }));
    }
  };

  const handleIsReplanishableChange = async (productId, replenishableValue) => {
    if (access === "edit") {
      await dispatch(updateReplenishableByProduct({
        productId,
        isReplenishable: replenishableValue || false,
        requestToUpdate: updateSingleProductInGroup,
      }));
    }
  };

  const onClickSidebarVariantView = (variant) => {
    setSelectedProduct(variant);
    setProductType(ProductType.PRODUCT_VARIANT);
  }
 
  const toggleRowExpansion = (productId) => {
    setExpandedRows(prev => ({
      ...prev,
      [productId]: !prev[productId]
    }));
  };

  // Early return if no products are provided
  if (!products || products.length === 0) {
    return <p>No products to display.</p>;
  }

  return (
    <div className={`flex transition-all duration-300 ease-in-out`}>
      <div className={`transition-all duration-300 ease-in-out w-full`}>
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10"></th>
                <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Image</th>
                <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product Name</th>
                <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Lead Time</th>
                <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                {access === "edit" && (
                  <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[220px]">Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {productsState.map((product, index) => (
                <React.Fragment key={product.id}>
                  <tr
                    className={`
                      border-b border-gray-200 last:border-b-0 hover:bg-blue-50 
                      cursor-pointer
                      ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}
                      ${selectedProduct?.id === product.id ? '!bg-blue-50' : ''}
                    `}
                    onClick={() => handleRowClick(product)}
                    onKeyDown={handleRowKeyDown(product)}
                    tabIndex={0}
                    role="button"
                    aria-label={`Select product ${product.name}`}
                  >
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {arrayHasItems(product.variants) && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleRowExpansion(product.id);
                          }}
                          className="text-gray-400 hover:text-gray-600 focus:outline-none"
                        >
                          {expandedRows[product.id] ? (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          ) : (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          )}
                        </button>
                      )}
                    </td>
                    <td className="py-2 px-4 w-[85px]">
                      {product.image ? (
                        <div className="w-12 h-12 relative rounded-md overflow-hidden bg-gray-100 border border-gray-200 flex items-center justify-center">
                          <img
                            src={product.image}
                            alt={product.name}
                            className="max-w-full max-h-full object-contain"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = 'https://placehold.co/40x40/e6e6e6/cccccc?text=No+Image';
                            }}
                          />
                        </div>
                      ) : (
                        <div className="w-12 h-12 flex items-center justify-center rounded-md bg-gray-100 border border-gray-200">
                          <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                      )}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-900">{product.name}</td>
                    <td className="py-3 px-4 text-sm text-gray-500">{product.lead_time}</td>
                    <td className="py-3 px-4 text-sm text-gray-500">{product.category}</td>
                    {access === "edit" && (
                      <td className="py-3 px-4 text-sm text-gray-500">
                        <div className="flex justify-end space-x-2">
                          <ConnectVariantButton
                            onClickCallback={(e) => {
                              e.stopPropagation();
                              setModalCurrentGroup(product);
                              setIsVariantModalOpen(true);
                            }}
                          />
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteGroup(product.id);
                            }}
                            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-black hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 rounded"
                            aria-label={`Delete product group ${product.name}`}
                            title={`Delete product group ${product.name}`}
                          >
                            {deleteGroupIsLoading() ? (
                              <svg className="animate-spin h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                            ) : (
                              <TrashIcon className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                  {expandedRows[product.id] && arrayHasItems(product.variants) && (
                    <tr>
                      <td colSpan="6" className="pl-[50px]">
                        <div className="overflow-x-auto relative">
                          {(deleteVariantIsLoading() || addVariantIsLoading()) && (<OverlayGrayBackground />)}
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-100">
                              <tr>
                                <th className="w-[85px] px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Image</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[400px] max-w-[400px]">Name</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SKU</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Barcode</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amazon</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Replenishable</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kit</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Color</th>
                                {access === "edit" && (
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                )}
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {product.variants?.map((variant) => (
                                <tr
                                  key={variant.id}
                                  onClick={(e) => {
                                    const deleteBtn = deleteButtonRef.current[variant.id];
                                    if (deleteBtn && (deleteBtn === e.target || deleteBtn.contains(e.target))) return;
                                    handleVariantClick(variant);
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      handleVariantClick(variant);
                                    }
                                  }}
                                  tabIndex={0}
                                  role="button"
                                  aria-label={`Select product ${variant.name}`}
                                  className={`
                                    hover:bg-gray-50 cursor-pointer
                                    ${selectedProduct?.id === variant.id ? '!bg-blue-50' : ''}
                                  `}
                                >
                                  <td className="px-4 py-2">
                                    {variant.image ? (
                                      <div className="w-12 h-12 relative rounded-md overflow-hidden bg-gray-100 border border-gray-200 flex items-center justify-center">
                                        <img
                                          src={variant.image}
                                          alt={variant.name}
                                          className="max-w-full max-h-full object-contain"
                                          onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = 'https://placehold.co/40x40/e6e6e6/cccccc?text=No+Image';
                                          }}
                                        />
                                      </div>
                                    ) : (
                                      <div className="w-12 h-12 flex items-center justify-center rounded-md bg-gray-100 border border-gray-200">
                                        <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                      </div>
                                    )}
                                  </td>
                                  <td className="px-4 py-2 text-sm text-gray-900 min-w-[300px] max-w-[300px]">{variant.name}</td>
                                  <td className="px-4 py-2 text-sm text-gray-900">
                                    <div className="relative flex gap-2 flex-row">
                                      {variant.sku}
                                      {variant.inactive_skus && (
                                        <>
                                          <InformationCircleIcon className='h-5 w-5 text-blue-600 hover:text-blue-900' data-tip={true} data-for={`${variant.sku}_${variant.id}`} />
                                          <ReactTooltip
                                            id={`${variant.sku}_${variant.id}`}
                                            html={true}
                                            effect='solid'
                                          >
                                            {`<div>
                                              <p className="text-xl">Inactive SKU'S:</p>
                                              ${(variant.inactive_skus).split(',').map((key) => (
                                              `<div key={${key}}>
                                                  <span>${key}</span>
                                                </div>`
                                            )).join('')}
                                            </div>`}
                                          </ReactTooltip>
                                        </>
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-4 py-2 text-sm text-gray-500">{variant.barcode}</td>
                                  <td className="px-4 py-2 text-sm">
                                    {/* <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(variant.status)}`}>
                                      {enumInventoryPhasedStatus[variant.status] || null}
                                    </span> */}
                                    <button 
                                      className="w-24"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <StatusSelect
                                        key={variant.status}
                                        idItem={variant.id}
                                        access={access}
                                        currentStatus={variant.status}
                                        handleStatusChange={handleStatusChange}
                                        isDisabled={productStatusIsLoading()}
                                      />
                                    </button>
                                  </td>
                                  <td className="px-4 py-2 text-sm text-gray-500 relative">
                                    {/* {renderBoolean(variant.is_amazon || variant.is_amazon_manually)} */}
                                    <button
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <IsAmazon
                                        key={`${variant?.isAmazon}_${variant.isAmazonManually}`}
                                        product={variant}
                                        access={access}
                                        withoutLoader={true}
                                        isLoading={productAmazonIsLoading()}
                                        groupUpdate={updateSingleProductInGroup}
                                      />
                                    </button>
                                  </td>
                                  <td className="px-4 py-2 text-sm text-gray-500 relative">
                                    <button
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <IsReplanishable
                                        key={variant?.isReplenishable}
                                        product={variant}
                                        access={access}
                                        disabled={variant.status !== ACTIVE}
                                        withoutLoader={true}
                                        isLoading={productReplanishableIsLoading()}
                                        groupUpdate={updateSingleProductInGroup}
                                      />
                                    </button>
                                  </td>
                                  <td className="px-4 py-2 text-sm text-gray-500">{Boolean(variant.kits) || Boolean(variant.is_kit_manually) ? "Y" : "N"}</td>
                                  <td className="px-4 py-2 text-sm text-gray-500">{variant.color}</td>
                                  {access === "edit" && (
                                    <td className="px-4 py-2 text-sm text-gray-500">
                                      <div className="flex gap-2 w-full justify-end">
                                        <button
                                          ref={el => deleteButtonRef.current[variant.id] = el}
                                          onClick={(e) => {
                                            e.preventDefault();
                                            handleDeleteVariant(variant.id, variant.group_id, product.vendor_id)
                                          }}
                                          title="Disconnect variant"
                                          className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-black hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 rounded disabled:opacity-50"
                                          aria-label="Disconnect variant"
                                        >
                                          <XMarkIcon className="h-4 w-4 currentColor" />
                                        </button>
                                      </div>
                                    </td>
                                  )}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <InventoryProductsSidebar
        isOpen={selectedProduct || openCreationVariant}
        access={access}
        selectedTab={selectedTab}
        currentGroup={currentGroup}
        creationVariant={openCreationVariant}
        selectedProduct={selectedProduct}
        productType={productType}
        onChangeSelectedTab={(type, tab) => {
          setSelectedTab((prev) => {
            return {
              ...prev,
              [type]: tab
            }
          })
        }}
        onClickSidebarVariantView={onClickSidebarVariantView}
        onSuccessSubmit={() => {
          onSaveCreation();
          onCloseProductCreation();
          setSelectedProduct(null);
          setProductType(null);
          setCurrentGroup("");
        }}
        onClose={() => {
          onCloseProductCreation();
          setSelectedProduct(null);
          setProductType(null);
          setCurrentGroup("");
        }}
      />
      {/* {selectedProduct && (
        <ProductSidebarContainer 
          product={selectedProduct}
          productType={productType}
          onClickSidebarVariantView={onClickSidebarVariantView}
          onClose={() => {
            setSelectedProduct(null);
            setProductType(null);
          }}
        />
      )} */}
      
      <VariantModal
        isOpen={access === "edit" && isVariantModalOpen}
        onClose={() => {
          setIsVariantModalOpen(false);
          setModalCurrentGroup(null);
        }}
        onAddVariant={handleAddVariant}
      />

      <ConfrimModal
        isOpen={access === "edit" && deleteVariantModalOpened}
        title={"Are you sure you want to remove this variant from the group?"}
        confirmButtonTitle={"Remove"}
        isLoading={deleteVariantIsLoading()}
        submitClick={async () => {
          setDeleteVariantModalOpened(false);
          await dispatch(deleteVariantFromGroup({
            productId: variantIdToDelete,
            groupId: groupIdForVariantDelete,
            vendorId: vendorIdToDelete,
            deleteManually: true
          }));
          setVariantIdToDelete("");
          setGroupIdForVariantDelete("");
          setVendorIdToDelete("");
        }}
        cancelClick={() => {
          setDeleteVariantModalOpened(false);
          setVariantIdToDelete("");
          setGroupIdForVariantDelete("");
          setVendorIdToDelete("");
        }}
      />

      <ConfrimModal
        isOpen={access === "edit" && deleteGroupModalOpened}
        title={"Are you sure you want to delete this product? This will remove the product and disconnect all connected variants."}
        confirmButtonTitle={"Delete Group"}
        isLoading={deleteGroupIsLoading()}
        submitClick={async () => {
          setDeleteGroupModalOpened(false);
          await dispatch(deleteGroup({
            groupId: groupIdToDelete,
            deleteManually: true
          }));
          setGroupIdToDelete("");
        }}
        cancelClick={() => {
          setDeleteGroupModalOpened(false);
          setGroupIdToDelete("");
        }}
      />
    </div>
  );
};

export default ProductsTable; 